import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, ClockIcon, Loader2Icon } from 'lucide-react';
import { loan } from '../../data/loan';
import { providers } from '../../data/providers';
import type { ProviderId, RunMode, RunStatus } from '../../types/aus';
import { ProviderMark } from './ProviderMark';
interface ProcessingPanelProps {
  mode: RunMode;
  statuses: Record<ProviderId, RunStatus>;
}
const order: ProviderId[] = ['fannie', 'freddie'];
export function ProcessingPanel({
  mode,
  statuses
}: ProcessingPanelProps) {
  const isDual = mode === 'dual';
  const activeProvider = order.find(id => statuses[id] === 'running') ?? (isDual ? 'freddie' : mode as ProviderId);
  const config = providers[activeProvider];
  const completed = order.filter(id => statuses[id] === 'complete').length;
  return <section aria-live="polite" aria-busy="true" className="rounded-2xl border border-slate-200 bg-white px-6 py-14 shadow-sm" data-id="element-145" data-mp="2wfk43iv62hd0">
      <div className="mx-auto flex max-w-md flex-col items-center text-center" data-id="element-146" data-mp="3l4wfv10e2hj7">
        <ProviderMark provider={activeProvider} size="lg" data-id="element-147" data-mp="3l6prkupuzry2" />

        <h2 className="mt-5 flex items-center gap-2 font-display text-lg font-bold tracking-tight text-graphite-800" data-id="element-148" data-mp="3g73dkj27i7o7">
          <Loader2Icon aria-hidden="true" className="h-5 w-5 animate-spin text-slate-400" data-id="element-149" data-mp="3v4dmbkngszg8" />
          {config.processingLabel}
        </h2>

        {isDual && <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-brand-700" data-id="element-150" data-mp="7xkzy2jondja">
            Dual AUS run · Engine {Math.min(completed + 1, 2)} of 2
          </p>}

        <p className="mt-2 text-sm text-slate-500" data-id="element-151" data-mp="hopex29cyye4">
          {loan.borrower} · Loan #{loan.loanNumber}
        </p>

        <div role="progressbar" aria-label="Submission progress" className="mt-7 h-1.5 w-full overflow-hidden rounded-full bg-slate-100" data-id="element-152" data-mp="cotd99o4vj1q">
          <motion.div className={`h-full w-1/3 rounded-full ${config.accentSolid}`} animate={{
          x: ['-100%', '300%']
        }} transition={{
          duration: 1.4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-153" data-mp="mnir8rcwsjcp" />
        </div>

        {isDual && <ul className="mt-6 w-full space-y-2 text-left" data-id="element-154" data-mp="yn7esh3b8zxx">
            {order.map(id => {
          const status = statuses[id];
          return <li key={id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-2.5" data-id="element-155" data-mp="18g50ew3ln50r">
                  <span className="flex items-center gap-2.5 text-sm font-medium text-slate-700" data-id="element-156" data-mp="1hf8ipxuxzge7">
                    <ProviderMark provider={id} size="sm" data-id="element-157" data-mp="1meuv652y4sly" />
                    {providers[id].name} {providers[id].shortLabel}
                  </span>
                  {status === 'complete' ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700" data-id="element-158" data-mp="3o79acwe3viyo">
                      <CheckCircle2Icon aria-hidden="true" className="h-4 w-4" data-id="element-159" data-mp="3t6dlvtoi5ss6" />
                      Complete
                    </span> : status === 'running' ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700" data-id="element-160" data-mp="25jhkm9906pjn">
                      <Loader2Icon aria-hidden="true" className="h-4 w-4 animate-spin" data-id="element-161" data-mp="21j7g710v076w" />
                      Submitting
                    </span> : <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400" data-id="element-162" data-mp="2t92a7aznwl1w">
                      <ClockIcon aria-hidden="true" className="h-4 w-4" data-id="element-163" data-mp="2p779a7ln89on" />
                      Queued
                    </span>}
                </li>;
        })}
          </ul>}

        <p className="mt-6 text-sm text-slate-500" data-id="element-164" data-mp="2f8hpuae3mh6a">
          This normally takes a few moments. Please keep this page open.
        </p>
      </div>
    </section>;
}